/**
 * Staffing, honestly: a building's total nurse hours per resident day (reported, CMS PBJ)
 * against the median of its same-type, same-state peers (the same reported column,
 * GET /facilities/benchmarks). There is no federal staffing floor (the 2024 minimum was
 * rescinded; none since 2026-02-02), so nothing here is ever measured against one.
 */
import { useEffect, useState } from 'react';
import { intelBenchmarks, type ApiClient } from '@aithora/shared';

export interface PeerStaffing {
  /** Median total nurse HPRD of the peer group, or null when the group has none. */
  median: number | null;
  n: number | null;
  cohort: string;
}

const keyOf = (type: string, state: string | null | undefined) => `${type}|${state ?? ''}`;

/** The peer medians for each distinct type and state asked for, loaded once each. */
export function usePeerStaffing(client: ApiClient, groups: { type: string; state: string | null | undefined }[]): Record<string, PeerStaffing | undefined> {
  const keys = [...new Set(groups.map((g) => keyOf(g.type, g.state)))].sort();
  const sig = keys.join(',');
  const [out, setOut] = useState<Record<string, PeerStaffing | undefined>>({});
  useEffect(() => {
    const ctrl = new AbortController();
    let live = true;
    Promise.all(
      keys.map(async (k) => {
        const [type, state] = k.split('|');
        try {
          const b = await intelBenchmarks(client, { query: { type, ...(state ? { state } : {}) }, signal: ctrl.signal });
          const m = b.metrics.totalHprd;
          const med = m && !m.suppressed && m.p50 != null && Number.isFinite(Number(m.p50)) ? Number(m.p50) : null;
          return [k, { median: med, n: m?.n ?? null, cohort: `${type}${state ? ' in ' + state : ' nationally'}` }] as const;
        } catch {
          return [k, undefined] as const;
        }
      }),
    ).then((rows) => {
      if (live) setOut(Object.fromEntries(rows));
    });
    return () => {
      live = false;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, sig]);
  return out;
}

export const peerOf = (map: Record<string, PeerStaffing | undefined>, type: string, state: string | null | undefined) => map[keyOf(type, state)];

/** "3.12 vs 3.55 typical for peers" (or just the figure when the peer group has none). */
export function staffingLine(hprd: number, peer: PeerStaffing | undefined): string {
  if (!peer || peer.median == null) return `${hprd.toFixed(2)}`;
  return `${hprd.toFixed(2)} vs ${peer.median.toFixed(2)} typical for peers`;
}
